import React, { ReactNode, FC } from 'react';
import ComponentErrorBoundary from '../../common/ComponentErrorBoundary';
import { UIDSLComponent } from '@dreamai/types';
import { useThreadLoading } from '../../../contexts/ThreadLoadingContext';
import { QRCodeSVG } from 'qrcode.react';
import 'chart.js/auto';
import { processProps } from './utils/stringToFunctionUtils';

import {
  ChartWrapper,
  Heading,
  Text,
  CodeComponent,
  MarkdownComponent,
  FormLabelComponent,
  ButtonComponent,
  TableComponent,
  FormControlComponent,
  FormCheckComponent,
  FormSelectComponent,
  SVGComponent,
  EmailComponent,
  EmailListTableView,
  SimpleListOfEmails,
  InlineWebview,
  EmployeePicker,
  CampaignOverview,
  CampaignMessaging,
  CampaignTargets,
  CampaignSettings,
  CampaignPlanComponent,
  TargetsHighlightComponent,
  Prospect,
  ProspectsToApprove,
  SourcingStrategyApprovalView,
  ConfirmationCarousel,
  ProDocTemplateEditor,
  HumanTaskDisplay,
  HumanTaskPhoneDialer,
  SignatureView,
  VocabularyList,
} from './dsl-components';
// Import InboxComponent directly to avoid circular dependency
import InboxComponent from './dsl-components/InboxComponent';

// Create the component map with only the supported DSL components
// We need to use Record<string, ...> instead of Record<UIDSLComponentType, ...> because
// TypeScript would require all UIDSLComponentType keys to be present
export const componentMap: Record<string, React.ComponentType<any> | string> = {
  // DSL components - only include components supported by our DSL

  // Custom DSL components
  Heading: Heading,
  Text: Text,
  Code: CodeComponent,
  Markdown: MarkdownComponent,
  Email: EmailComponent,
  EmailListTableView: EmailListTableView,
  SimpleListOfEmails: SimpleListOfEmails,
  InlineWebview: InlineWebview,
  Inbox: InboxComponent,
  EmployeePicker: EmployeePicker,
  CampaignOverview: CampaignOverview,
  CampaignMessaging: CampaignMessaging,
  CampaignTargets: CampaignTargets,
  CampaignSettings: CampaignSettings,
  CampaignPlan: CampaignPlanComponent,
  TargetsHighlight: TargetsHighlightComponent,
  Prospect: Prospect,
  ProspectsToApprove: ProspectsToApprove,
  SourcingStrategyApprovalView: SourcingStrategyApprovalView,
  ConfirmationCarousel: ConfirmationCarousel,
  ProDocTemplateEditor: ProDocTemplateEditor,
  HumanTaskDisplay: HumanTaskDisplay,
  HumanTaskPhoneDialer: HumanTaskPhoneDialer,
  SignatureView: SignatureView,
  VocabularyList: VocabularyList,

  // Form components
  'Form.Label': FormLabelComponent,
  'Form.Control': FormControlComponent,
  'Form.Check': FormCheckComponent,
  'Form.Select': FormSelectComponent,

  // Table component
  Table: TableComponent,

  // Button component
  Button: ButtonComponent,

  // Chart component (from react-chartjs-2)
  Chart: ChartWrapper,

  // QR code component
  QRCodeSVG: QRCodeSVG,

  // SVG component
  SVG: SVGComponent,
};

/**
 * Parse JSX-like string into a structured format
 * @param jsxString - JSON string representation of a UI component
 * @returns Parsed UIComponent or null if parsing fails
 */
export const parseJSX = (jsxString: string): UIDSLComponent | null => {
  try {
    // This assumes the backend is sending a JSON representation of the UI
    const parsedComponent = JSON.parse(jsxString);
    return parsedComponent;
  } catch (error) {
    console.error('Error parsing JSX string:', error);
    return null;
  }
};

/**
 * Interface for the user input callback function
 */
export interface UserInputCallbackParams {
  viewId: string;
  clickedElement: UIDSLComponent;
  section?: string;
  formValues?: Record<string, any>; // Now optional as we get values from the section in ChatCanvas
}

/**
 * UIUpdate Component
 *
 * A component that manages form state for a UI update and renders the component tree.
 */
export interface UIUpdateProps {
  component: UIDSLComponent;
  viewId: string;
  threadId: string | null;
  section: string;
  onUserInput: (params: UserInputCallbackParams) => void;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
  isInboxView?: boolean;
  onInboxViewComponentViewClick?: (componentId: string) => void;
}

export const UIUpdate: FC<UIUpdateProps> = ({
  component,
  viewId,
  threadId,
  section,
  onUserInput,
  updateThreadViewVisualDescription,
  isInboxView,
  onInboxViewComponentViewClick,
}) => {
  // Get loading state and map from context
  const { loadingThreads } = useThreadLoading();
  const loading = threadId ? !!loadingThreads[threadId] : false;

  // Handler for user input (button clicks)
  const handleUserAction = (
    clickedElement: UIDSLComponent,
    formValues?: Record<string, any>,
  ) => {
    onUserInput({
      viewId,
      clickedElement,
      section,
      formValues,
    });
  };

  return (
    <>
      {renderComponent(
        component,
        `component-${component.id || 'root'}`,
        handleUserAction,
        loading,
        threadId,
        updateThreadViewVisualDescription,
        isInboxView,
        onInboxViewComponentViewClick,
      )}
    </>
  );
};

/**
 * Render a component tree recursively
 * @param component - The component to render
 * @param key - React key for the component
 * @param onUserAction - Callback for user actions
 * @param isLoading - Whether the component is in a loading state
 * @param threadId - Thread ID to pass to components that need it
 * @param updateThreadViewVisualDescription - Function to update thread visual description
 * @param isInboxView - Whether the component is being rendered in inbox view
 * @param onInboxViewComponentViewClick - Callback for inbox view component clicks
 * @returns React node
 */
const renderComponent = (
  component: UIDSLComponent,
  key: string,
  onUserAction: (
    component: UIDSLComponent,
    formValues?: Record<string, any>,
  ) => void,
  isLoading: boolean,
  threadId?: string | null,
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>,
  isInboxView?: boolean,
  onInboxViewComponentViewClick?: (componentId: string) => void,
): ReactNode => {
  const { type, props } = component;

  // Create a new props object to avoid modifying the original
  // Process props to check for stringified functions and convert special strings
  const newProps = processProps({ ...props });

  // Add threadId to all components
  if (threadId) {
    newProps.threadId = threadId;
  }

  // Add updateThreadViewVisualDescription to all components
  if (updateThreadViewVisualDescription) {
    newProps.updateThreadViewVisualDescription =
      updateThreadViewVisualDescription;
  }

  // Add isInboxView to all components
  if (isInboxView !== undefined) {
    newProps.isInboxView = isInboxView;
  }

  // Add onInboxViewComponentViewClick to all components
  if (onInboxViewComponentViewClick) {
    newProps.onInboxViewComponentViewClick = onInboxViewComponentViewClick;
  }

  // For all buttons and elements that might submit forms, prevent default behavior
  // Skip this handling for Button components rendered as anchor tags
  if (type === 'Button') {
    // Disable button when thread is loading
    newProps.disabled = isLoading || newProps.disabled;

    const originalOnClick = newProps.onClick;
    newProps.onClick = (event: React.MouseEvent) => {
      event.preventDefault();
      if (originalOnClick && typeof originalOnClick === 'function') {
        originalOnClick(event);
      }
    };
  }

  // Handle reactOnUserClick property if present
  if (type === 'Button' && 'reactOnUserClick' in props) {
    // Keep the reactOnUserClick prop for our ButtonComponent
    // but also add an onClick handler that will be passed to ButtonComponent

    // Disable interactive elements when thread is loading
    newProps.disabled = isLoading || newProps.disabled;

    // Add onClick handler to the props that will be passed to our ButtonComponent
    // This will collect form values from the registry when clicked
    newProps.onClick = (data: any) => {
      // The data object contains formValues collected from the form registry
      onUserAction(component, data.formValues);
    };

    // Add a visual indicator that this element is clickable
    if (!newProps.className) {
      newProps.className = 'clickable-element';
    } else {
      newProps.className += ' clickable-element';
    }
  }

  // Handle value props for our DSL components
  let renderedChildren: ReactNode = null;

  // Set renderedChildren to null by default since most components don't have children
  renderedChildren = null;

  // Prepare the component to be rendered
  let componentToRender;

  // Component definitions have been moved to individual files in the dsl-components folder
  // These components are imported at the top of the file;

  // Get the component from the component map
  const Component = componentMap[type];

  // If component doesn't exist in the map, return an error message
  if (!Component) {
    return (
      <div className='error-boundary p-2 border border-danger rounded'>
        <p className='text-danger mb-1'>Unknown component type: {type}</p>
      </div>
    );
  }

  // Check if the component is a string (HTML element) or React component
  if (typeof Component === 'string') {
    // For standard HTML elements, use React.createElement
    componentToRender = React.createElement(
      Component,
      { key, ...newProps },
      renderedChildren,
    );
  } else {
    // For React components, use JSX
    componentToRender = (
      <Component key={key} {...newProps}>
        {renderedChildren}
      </Component>
    );
  }

  // Wrap the component in an error boundary
  return (
    <ComponentErrorBoundary componentName={type} key={`error-boundary-${key}`}>
      {componentToRender}
    </ComponentErrorBoundary>
  );
};
